// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import translate from "@insite/client-framework/Translate";
import { TraitValueModel, VariantTraitModel } from "@insite/client-framework/Types/ApiModels";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
// import Swatch, { SwatchTypeValues } from "@insite/mobius/Swatch"; // Starter Custom Code
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { injectCss } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
// import React from "react"; // Foundation
import styled, { css } from "styled-components";

/* Starter Custom Code - Start */
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
// import Swatch, { SwatchTypeValues } from "@store/MobiusOverrides/Swatch/Swatch"; // Foundation
import { swatchListItemStyles, swatchListStyles } from "@store/Themes/common/mixins/swatch";
import { swatchProps } from "@store/Themes/common/props/swatch";
import useSwatchSelectedAccessibleText from "@toolkit/Framework/Common/Hooks/useSwatchSelectedAccessibleText";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
/* Starter Custom Code - End */

/* Store Custom Code - Start */
import Swatch, { SwatchTypeValues } from "@store/MobiusOverrides/Swatch/Swatch";
import React, { useEffect } from "react";
/* Store Custom Code - End */

interface Props {
    variantSelection: SafeDictionary<string>;
    variantTrait: VariantTraitModel;
    updateVariantSelection: (traitValueId: string, variantTraitId: string) => void;
    // Foundation
    disableClearSelection: boolean;
}

export interface ProductDetailsVariantGridStyles {
    labelText?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    labelWrapper?: InjectableCss;
    swatchClickable?: ClickablePresentationProps;
    swatchCaptionProps?: InjectableCss;
    // Starter Custom Code
    wrapper?: InjectableCss;
}

export const variantGridStyles: ProductDetailsVariantGridStyles = {
    labelText: {
        css: css`
            font-size: 15px;
            font-weight: 600;
        `,
    },
    labelWrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    swatchClickable: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
        `,
    },
    link: {
        color: "primary",
        css: css`
            margin: 10px 5px;
            margin-left: ${pxToRem(40)};
        `,
    },
    swatchCaptionProps: {
        css: css`
            width: 100%;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            text-align: center;
        `,
    },
    wrapper: swatchProps.wrapper,
};

const SwatchGridDisplayItem = styled.li`
    ${swatchListItemStyles() /* Starter Custom Code */}
`;

export const UnorderedList = styled.ul<InjectableCss>`
    ${swatchListStyles() /* Starter Custom Code */}
    ${injectCss}
`;

const styles = variantGridStyles;

const ProductDetailsVariantSwatchGrid = ({
    variantSelection,
    updateVariantSelection,
    variantTrait,
    disableClearSelection,
}: Props) => {
    const updateVariantSelectionSwatchGrid = (traitValueId?: string) => {
        updateVariantSelection(traitValueId ?? "", variantTrait.id);
    };

    const swatchSelectedAccessibleText = useSwatchSelectedAccessibleText(variantTrait); // Starter Custom Code

    // Foundation -->
    useEffect(() => {
        if (variantTrait.traitValues && variantTrait.traitValues.length <= 1) {
            updateVariantSelectionSwatchGrid(variantTrait.traitValues[0].id);
        }
    }, []);
    // <-- Foundation

    const CreateSwatchGrid = (traitValue: TraitValueModel) => {
        const isSelected = variantSelection[variantTrait.id] === traitValue.id; // Starter Custom Code

        return (
            <SwatchGridDisplayItem key={traitValue.id}>
                <Clickable
                    onClick={() => updateVariantSelectionSwatchGrid(isSelected ? "" : traitValue.id)} // Starter Custom Code
                    {...styles.swatchClickable}
                    disabled={traitValue.isDisabled}
                    // Starter Custom Code
                    aria-checked={isSelected}
                    role="radio"
                    data-selected={isSelected || undefined}
                    title={
                        traitValue.valueDisplay && !variantTrait.displayTextWithSwatch
                            ? traitValue.valueDisplay
                            : undefined
                    }
                >
                    <Swatch
                        type={traitValue.swatchType as SwatchTypeValues}
                        value={
                            traitValue.swatchType === SwatchTypeValues.Image
                                ? traitValue.swatchImageValue
                                : traitValue.swatchColorValue
                        }
                        // Foundation -->
                        size={50}
                        // <-- Foundation
                        caption={`${variantTrait.nameDisplay}: ${traitValue.valueDisplay || traitValue.value}`} // Foundation
                        isDisabled={traitValue.isDisabled}
                        captionProps={styles.swatchCaptionProps}
                        // Starter Custom Code
                        isSelected={isSelected}
                        hasTextCaption={variantTrait.displayTextWithSwatch} // Foundation
                    />
                </Clickable>
            </SwatchGridDisplayItem>
        );
    };

    return (
        <StyledWrapper {...styles.wrapper /* Starter Custom Code */} key={variantTrait.id}>
            <StyledWrapper {...styles.labelWrapper}>
                <Typography {...styles.labelText}>
                    {variantTrait.nameDisplay}
                    {/* Starter Custom Code - Start */}
                    {swatchSelectedAccessibleText && (
                        <VisuallyHidden>{` ${swatchSelectedAccessibleText}`}</VisuallyHidden>
                    )}
                    {/* Starter Custom Code - End */}
                </Typography>
                {/* Starter Removed - Clear Selection */}
            </StyledWrapper>
            <UnorderedList role="radiogroup" aria-label={variantTrait.nameDisplay} /* Starter Custom Code */>
                {variantTrait.traitValues?.map(traitValue => CreateSwatchGrid(traitValue))}
            </UnorderedList>
        </StyledWrapper>
    );
};
export default ProductDetailsVariantSwatchGrid;
